import axios from 'axios';
import cookie from 'vue-cookies';
import { REFERRAL_AGENT_CODE, REFERRAL_AGENT_SNAPSHOT } from '@/utils/const';

const REFERRAL_COOKIE_DAYS = 7;
const SNAPSHOT_TTL_MS = REFERRAL_COOKIE_DAYS * 24 * 60 * 60 * 1000;

export function clearReferralSnapshot() {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.removeItem(REFERRAL_AGENT_SNAPSHOT);
  } catch (e) {
    // ignore
  }
}

export function readReferralSnapshot() {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(REFERRAL_AGENT_SNAPSHOT);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.code || !parsed?.agent) return null;
    if (parsed.savedAt && (Date.now() - Number(parsed.savedAt)) > SNAPSHOT_TTL_MS) {
      clearReferralSnapshot();
      return null;
    }
    return {
      code: String(parsed.code).trim(),
      agent: parsed.agent,
      source: parsed.source === 'manual' ? 'manual' : 'link',
    };
  } catch (e) {
    return null;
  }
}

/**
 * Read raw referral code from route query, cookie, or persisted snapshot (query wins).
 * @param {Object} [routeQuery]
 * @returns {string|null}
 */
export function getReferralAgentCode(routeQuery) {
  const fromQuery = routeQuery && routeQuery.agentCode;
  if (fromQuery && typeof fromQuery === 'string' && fromQuery.trim()) {
    return fromQuery.trim();
  }
  const fromCookie = cookie.get(REFERRAL_AGENT_CODE);
  if (fromCookie && typeof fromCookie === 'string' && fromCookie.trim()) {
    return fromCookie.trim();
  }
  const snapshot = readReferralSnapshot();
  if (snapshot?.code) {
    return String(snapshot.code).trim();
  }
  return null;
}

export function persistReferralAgentCode(code) {
  const trimmed = String(code || '').trim();
  if (!trimmed) return;
  cookie.set(REFERRAL_AGENT_CODE, trimmed, REFERRAL_COOKIE_DAYS);
}

export function clearReferralAgentCode() {
  cookie.remove(REFERRAL_AGENT_CODE);
  clearReferralSnapshot();
}

/**
 * Persist validated referral agent so product navigations / new tabs keep attribution.
 * @param {{ code: string, agent: Object, source?: string }} payload
 */
export function persistReferralSnapshot({ code, agent, source } = {}) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  const trimmed = String(code || '').trim();
  if (!trimmed || !agent) return;
  try {
    window.localStorage.setItem(REFERRAL_AGENT_SNAPSHOT, JSON.stringify({
      code: trimmed,
      agent,
      source: source || 'link',
      savedAt: Date.now(),
    }));
  } catch (e) {
    // ignore quota / private mode
  }
  persistReferralAgentCode(trimmed);
}

/**
 * Attach agentCode to a Vue Router location so buy journeys keep the referral in the URL.
 * @param {string|Object} routeLocation
 * @param {string|null} [agentCode]
 */
export function withReferralQuery(routeLocation, agentCode) {
  const code = String(agentCode || getReferralAgentCode() || '').trim();
  if (!routeLocation) return routeLocation;
  if (!code) return routeLocation;

  if (typeof routeLocation === 'string') {
    return { path: routeLocation, query: { agentCode: code } };
  }

  return {
    ...routeLocation,
    query: {
      ...(routeLocation.query || {}),
      agentCode: code,
    },
  };
}

function mapReferralAgent(data) {
  if (!data || (data.agencyCode == null && data.agencyName == null && !data.accountNo)) {
    return null;
  }
  return {
    agencyCode: data.agencyCode != null ? String(data.agencyCode) : '',
    agencyName: data.agencyName != null ? String(data.agencyName) : '',
    accountNo: data.accountNo != null ? String(data.accountNo).trim() : '',
    referralCode: data.referralCode != null ? String(data.referralCode).trim() : '',
    referralLink: data.referralLink != null ? String(data.referralLink).trim() : '',
  };
}

function agentMatchesCode(agent, code) {
  if (!agent || !code) return false;
  const needle = String(code).trim().toLowerCase();
  const fields = [agent.referralCode, agent.accountNo, agent.agencyCode];
  return fields.some((f) => f && String(f).trim().toLowerCase() === needle);
}

/**
 * List active referral agents (for customer search).
 * GET /customer/gis/coresetups/agency/referral
 * @returns {Promise<Array>}
 */
export async function listReferralAgents() {
  try {
    const response = await axios.get(
      '/customer/gis/coresetups/agency/referral',
      { _skipErrorRedirect: true },
    );
    if (response.status !== 200 || !response.data) return [];
    const raw = response.data.object != null ? response.data.object : response.data;
    const list = Array.isArray(raw) ? raw : [];
    return list.map(mapReferralAgent).filter(Boolean);
  } catch (err) {
    return [];
  }
}

/**
 * Validate referral code via GIS (by AGN_REFERRAL_CODE), with fallback match on
 * accountNo / agencyCode from the referral agent list (shared links often use shortDesc).
 */
export async function validateReferralCode(referralCode) {
  if (!referralCode || typeof referralCode !== 'string') return null;
  const code = referralCode.trim();
  if (!code) return null;
  try {
    const response = await axios.get(
      `/customer/gis/coresetups/agency/referral/${encodeURIComponent(code)}`,
      { _skipErrorRedirect: true },
    );
    if (response.status === 200 && response.data) {
      const data = response.data.object != null ? response.data.object : response.data;
      const mapped = mapReferralAgent(data);
      if (mapped) {
        if (!mapped.referralCode) mapped.referralCode = code;
        return mapped;
      }
    }
  } catch (err) {
    // fall through to list match
  }

  try {
    const list = await listReferralAgents();
    const match = list.find((a) => agentMatchesCode(a, code));
    if (!match) return null;
    if (!match.referralCode) match.referralCode = code;
    return match;
  } catch (err) {
    return null;
  }
}

/** Numeric GIS agency code for motor / PA payloads (0 = DIRECT). */
export function referralAgencyNumericId(agent) {
  if (!agent?.agencyCode) return 0;
  const n = Number(agent.agencyCode);
  return Number.isFinite(n) && !Number.isNaN(n) ? n : 0;
}

/** Mapfre / travel short description (not display name). */
export function referralAgentShortDesc(agent) {
  const shortDesc = String(agent?.accountNo || '').trim();
  if (shortDesc) return shortDesc;
  const code = String(agent?.referralCode || '').trim();
  return code || 'DIRECT';
}

function isLocalHost(hostname) {
  if (!hostname) return false;
  const host = String(hostname).toLowerCase();
  return host === 'localhost'
    || host === '127.0.0.1'
    || host.endsWith('.local')
    || /^192\.168\.\d+\.\d+$/.test(host)
    || /^10\.\d+\.\d+\.\d+$/.test(host);
}

/**
 * Build a shareable referral URL.
 * On local/dev hosts, rewrite GIS prod links to the current origin so agents can test.
 */
export function buildShareableReferralLink({ referralLink, agentCode } = {}) {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const local = typeof window !== 'undefined' && isLocalHost(window.location.hostname);

  let code = String(agentCode || '').trim();
  if (!code && referralLink) {
    try {
      const fromGis = new URL(referralLink, origin || 'https://selfservice.pacisinsurance.com');
      code = fromGis.searchParams.get('agentCode') || '';
    } catch (e) {
      // ignore parse errors
    }
  }

  if (local && code) {
    return `${origin}/?agentCode=${encodeURIComponent(code)}`;
  }
  if (referralLink && String(referralLink).trim()) {
    return String(referralLink).trim();
  }
  if (code && origin) {
    return `${origin}/?agentCode=${encodeURIComponent(code)}`;
  }
  return '';
}
